/** Tono de un toast del portal. Vive acá (no en `PortalPrimitives.tsx`) para
 * que este módulo no dependa de él — `PortalPrimitives.tsx` sí importa de
 * acá (`registerToastNotifier`/`unregisterToastNotifier`), y la dirección
 * inversa crearía un ciclo (`deps:check`, `no-circular-imports`). */
export type ToastTone = "success" | "neutral" | "error";

/**
 * Puente entre el `QueryClient` (creado en `main.tsx`, fuera del árbol de
 * `ToastProvider`) y el toast montado dentro de `PortalApp`. `MutationCache`
 * y `QueryCache` no tienen acceso a `useToast()` porque el cliente vive por
 * encima del provider (T2, `t2-foundation`): en vez de reestructurar el árbol
 * (mover el `QueryClient` rompería que `ValidatorApp` —fuera de
 * `SessionProvider`/`ToastProvider`— siga usando el mismo cliente), el
 * `ToastProvider` se registra acá al montar y se da de baja al desmontar; el
 * fallback del `QueryClient` llama a `notifyError` sin saber si hay un toast
 * montado. Sin notificador registrado (el árbol del validador, o durante un
 * desmontaje), `notifyError` es un no-op seguro: nunca puede tirar una
 * excepción dentro de un callback de la caché.
 */
type ToastNotifier = (
  message: string,
  tone: ToastTone,
  options?: {
    correlationId?: string | undefined;
    duration?: number | undefined;
  },
) => void;

let notifier: ToastNotifier | null = null;

export function registerToastNotifier(fn: ToastNotifier): void {
  notifier = fn;
}

/** Solo da de baja si `fn` sigue siendo el notificador activo: evita que un
 * remontaje (StrictMode) o un desmontaje fuera de orden borre el notificador
 * de una instancia más nueva. */
export function unregisterToastNotifier(fn: ToastNotifier): void {
  if (notifier === fn) notifier = null;
}

export function notifyError(
  message: string,
  options?: { correlationId?: string | undefined },
): void {
  notifier?.(message, "error", options);
}
